/**
 * @file  ModalManager/index.tsx
 * @description  controls the modal behavior and messages
 *
 * @example  <ModalManager />  use modal manager to service to set the message
 * @author ogarcia (ozkary)
 *
 */
// Modules
import React, { useCallback, useEffect, useState, Fragment } from 'react';
import Modal from '../../elements/Modal';
import * as ModalProps from '../../elements/Modal/styles';
import * as Content from './styles';
import Title from '../../elements/Title';
import PhoneLink from '../../elements/PhoneLink';
import List from '../../elements/List';

/**
 * manages the modal state
 * @param modal properties with actions and labels
 * @returns a modal popup
 */
const ModalManager = ({
  state,
  messages
}: Content.ModalManagerProps): JSX.Element => {
  const initialState: ModalProps.Props = {
    visible: false,
    title: ''
  };
  const [modal, setModal] = useState<ModalProps.Props>(initialState);
  const [phone, setPhone] = useState<string>('');
  /**
   * closes the dialog
   */
  const onCancel = useCallback(() => {
    setModal(initialState);
  }, [modal]);

  /**
   * handles the conitnue event, closes the modal dialog and calls the onOk action
   */
  const onContinue = useCallback(
    (state: Content.ModalManagerState) => {
      onCancel();
      if (state && state.onOk) {
        state.onOk();
      }
    },
    [state]
  );

  /**
   * Handles cancel event.
   */
  const handleCancel = useCallback(
    (state: Content.ModalManagerState) => {
      onCancel();
      if (state && state.onCancel) {
        state.onCancel();
      }
    },
    [state]
  );

  /**
   * Listens to modal changes
   */
  useEffect(() => {
    const name = state.name;
    const message: Content.ModalMessage = messages[name];
    if (message) {
      const fnOk = state.onOk ? () => onContinue(state) : state.onOk;
      const fnCancel = state.onCancel ? () => handleCancel(state) : onCancel;
      const settings: ModalProps.Props = {
        visible: true,
        title: message.title,
        okText: message.buttons?.ok?.title,
        cancelText: message.buttons?.cancel?.title,
        onOk: fnOk,
        onCancel: fnCancel,
        content: message.content,
        closable: state.closable,
        footer: message.footer,
        items: message.items,
        className: state?.className
      };
      setPhone(message.phone || '');
      setModal(settings);
    }
  }, [state]);

  return (
    <Modal
      title={modal?.title ?? ''}
      onCancel={modal?.onCancel}
      visible={modal?.visible ?? false}
      onOk={modal?.onOk}
      okText={modal?.okText}
      cancelText={modal?.cancelText}
      closable={modal?.closable ?? true}
      className={modal?.className ?? ''}
    >
      <Title level={5}>{modal?.content ?? ''}</Title>
      <>{state.body}</>
      {phone ? (
        <PhoneLink title={''} phone={phone} id="modal__phone-support" />
      ) : (
        <Fragment />
      )}
      {modal?.items && <List items={modal.items}/>}
      {modal?.footer && <Title className='modal__content-footer' level={5}>{modal.footer}</Title>}
    </Modal>
  );
};

export default ModalManager;
